import { useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import {
  Calculator,
  ChevronDown,
  Globe,
  ShoppingBag,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { AppLink } from "@/components/routing/AppLink";
import { getServicesNavData, type ServiceId } from "@/content/services";
import type { Locale } from "@/context/AppProviders";
import { cn } from "@/lib/utils";

const SERVICE_ICONS: Record<ServiceId, LucideIcon> = {
  ecommerce: ShoppingBag,
  quickSale: Zap,
  dealer: Users,
  accounting: Calculator,
  website: Globe,
};

export type ServicesNavItem = ReturnType<typeof getServicesNavData>[number];

export function useServicesNavItems(locale: Locale) {
  return getServicesNavData(locale);
}

function ServiceColumn({
  item,
  onClose,
}: {
  item: ServicesNavItem;
  onClose: () => void;
}) {
  const Icon = SERVICE_ICONS[item.id];

  return (
    <div className="min-w-0">
      <AppLink
        href={item.href}
        onClick={onClose}
        className="group mb-4 flex items-center gap-2.5 no-underline"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-enabase-blue/10 text-accent-link transition group-hover:bg-enabase-blue/15">
          <Icon className="size-4" strokeWidth={1.5} aria-hidden />
        </span>
        <span className="text-sm font-semibold leading-snug text-[var(--text)] transition group-hover:text-accent-link">
          {item.label}
        </span>
      </AppLink>

      <ul className="space-y-0.5 border-t border-[var(--border)] pt-3">
        {item.features.map((feature) => (
          <li key={feature.href}>
            <AppLink
              href={feature.href}
              onClick={onClose}
              className="block rounded-md px-2 py-1.5 text-[0.8125rem] leading-snug text-[var(--text-sm)] no-underline transition hover:bg-[var(--surface-inset)] hover:text-[var(--text)]"
            >
              {feature.title}
            </AppLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ServicesMegaMenu({
  open,
  menuRef,
  items,
  onClose,
}: {
  open: boolean;
  menuRef: RefObject<HTMLDivElement | null>;
  items: ServicesNavItem[];
  onClose: () => void;
}) {
  if (typeof document === "undefined") return null;

  return createPortal(
    open ? (
      <div ref={menuRef} className="fixed inset-0 top-16 z-[110] hidden lg:block">
        <button
          type="button"
          className="absolute inset-0 cursor-default bg-black/50 backdrop-blur-[2px]"
          aria-label="Menüyü kapat"
          onClick={onClose}
        />

        <div
          className="relative border-b border-[var(--border)] bg-[var(--bg2)] shadow-[0_24px_48px_-24px_rgba(0,0,0,0.45)]"
          role="menu"
        >
          <div className="mx-auto max-w-[var(--layout-max)] px-5 py-7 sm:px-7 lg:px-9">
            <div className="grid grid-cols-2 gap-x-8 gap-y-10 lg:grid-cols-3 xl:grid-cols-5 xl:gap-x-6 xl:gap-y-0">
              {items.map((item) => (
                <ServiceColumn key={item.id} item={item} onClose={onClose} />
              ))}
            </div>
          </div>
        </div>
      </div>
    ) : null,
    document.body
  );
}

function MobileServiceGroup({
  item,
  expanded,
  onToggle,
  onNavigate,
}: {
  item: ServicesNavItem;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const Icon = SERVICE_ICONS[item.id];

  return (
    <div className="border-b border-[var(--border)] last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-2 py-2.5 text-left text-sm font-medium text-[var(--text)]"
        aria-expanded={expanded}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <Icon className="size-4 shrink-0 text-accent-link" strokeWidth={1.5} aria-hidden />
          <span className="truncate">{item.label}</span>
        </span>
        <ChevronDown
          className={cn("size-4 shrink-0 opacity-50 transition-transform", expanded && "rotate-180")}
        />
      </button>
      {expanded ? (
        <div className="overflow-hidden">
          <div className="space-y-0.5 pb-2 pl-8 pr-2">
            {item.features.map((feature) => (
              <AppLink
                key={feature.href}
                href={feature.href}
                onClick={onNavigate}
                className="block rounded-md px-2 py-2 text-sm text-[var(--text-sm)] no-underline hover:bg-[var(--surface-inset)] hover:text-[var(--text)]"
              >
                {feature.title}
              </AppLink>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ServicesMobileMenu({
  open,
  onToggle,
  items,
  servicesLabel,
  onNavigate,
}: {
  open: boolean;
  onToggle: () => void;
  items: ServicesNavItem[];
  servicesLabel: string;
  onNavigate: () => void;
}) {
  const [expandedId, setExpandedId] = useState<ServiceId | null>(null);

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-lg px-2 py-2.5 text-sm font-medium text-[var(--text)]"
        onClick={onToggle}
        aria-expanded={open}
      >
        {servicesLabel}
        <ChevronDown
          className={cn("size-4 opacity-60 transition-transform", open && "rotate-180")}
        />
      </button>
      {open ? (
        <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface-inset)]/40">
          {items.map((item) => (
            <MobileServiceGroup
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId((current) => (current === item.id ? null : item.id))}
              onNavigate={() => {
                setExpandedId(null);
                onNavigate();
              }}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}
